import {ReactComponent as CloseIcon} from "assets/img/icons/close.svg";
import {BaseButton, ButtonPrimaryAltDark} from "components/Buttons";
import {Exist} from "components/Exist";
import {partial} from "lodash";
import {hideBanner} from "modules/actions";
import {getBanner} from "modules/selectors/messages";
import React, {useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useMedia} from "react-use";
import styled from "styled-components";

const Wrapper = styled.div`
	position: sticky;
	top: 0;
	background: #ffffff;
	z-index: 5;
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-width: 1260px;
	width: 100%;
	padding: 12px 30px;
	margin: 0 auto;
	box-sizing: border-box;

	@media (max-width: 840px) {
		padding: 8px 12px;
	}
`;

const Content = styled.div`
	display: flex;
	align-items: center;
	gap: 64px;

	svg {
		min-width: 32px;
		width: 32px;
		height: 32px;
	}

	@media (max-width: 1200px) {
		gap: 24px;
	}

	@media (max-width: 840px) {
		gap: 8px;
		align-items: flex-start;

		svg {
			width: 24px;
			min-width: 24px;
			height: 24px;
		}
	}
`;

const BannerContent = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;
	flex: 1;
	color: #1e1450;
	font-size: 16px;
	font-weight: 400;
	line-height: 135%;

	@media (max-width: 840px) {
		gap: 8px;
		font-size: 14px;
		align-items: flex-start;
	}
`;

const Texts = styled.div`
	flex: 1;
`;

const Actions = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin-left: auto;

	@media (max-width: 840px) {
		margin-left: 0;
	}
`;

const Title = styled.div`
	font-size: 20px;
	font-weight: 700;
	line-height: 140%;

	@media (max-width: 840px) {
		font-size: 16px;
	}
`;

// we don't know the image size, so we only set the maximum values
const Image = styled.img`
	max-height: 39px;
	max-width: 189px;

	@media (max-width: 840px) {
		max-height: 28px;
		max-width: 85px;
	}
`;

const Button = styled(ButtonPrimaryAltDark)`
	font-family: BrownPro, sans-serif;
	font-size: 16px;
	font-weight: 700;
	line-height: 150%;
`;

export const Banner: React.FC = () => {
	const isMobile = useMedia("(max-width: 840px)");
	const dispatch = useDispatch();
	const banner = useSelector(getBanner);
	const navigate = useNavigate();

	const closeHandler = useCallback(() => {
		dispatch(hideBanner());
	}, [dispatch]);

	const redirectHandler = useCallback(
		(url: string) => {
			navigate(url);
			closeHandler();
		},
		[navigate, closeHandler]
	);

	if (!banner) return null;

	const {title, Icon, description, image, redirectUrl} = banner;

	return (
		<Wrapper>
			<Container>
				<Content>
					<Exist when={Boolean(image) && !isMobile}>
						<Image src={image!} />
					</Exist>

					<BannerContent>
						{Icon && <Icon />}

						<Texts>
							<Title>{title}</Title>
							<div>{description}</div>
						</Texts>

						<Exist when={!isMobile}>
							<Exist when={Boolean(redirectUrl)}>
								<Button onClick={partial(redirectHandler, redirectUrl!)}>
									Play now
								</Button>
							</Exist>
						</Exist>
					</BannerContent>

					<BaseButton onClick={closeHandler}>
						<CloseIcon />
					</BaseButton>
				</Content>

				<Exist when={isMobile}>
					<Actions>
						<Exist when={Boolean(redirectUrl)}>
							<Button onClick={partial(redirectHandler, redirectUrl!)}>
								Play now
							</Button>
						</Exist>

						<Exist when={Boolean(image)}>
							<Image src={image!} />
						</Exist>
					</Actions>
				</Exist>
			</Container>
		</Wrapper>
	);
};
